import { EventTarget } from 'cc';

export enum EventType {
    DENOM_CHANGED = 'DENOM_CHANGED',
    UNIT_CHANGED = 'UNIT_CHANGED',
    CURRENCY_CHANGED = 'CURRENCY_CHANGED',
}

export enum Unit {
    Value,
    Dollar,
    Credit,
}

export enum Currency {
    USD = 'USD',
    CNY = 'CNY',
    THB = 'THB',
    SOL = 'SOL',
    BRL = 'BRL',
    IDR = 'IDR',
    JPY = 'JPY',
    KRW = 'KRW',
    RUB = 'RUB',
    TRY = 'TRY',
    VND = 'VND',
    EUR = 'EUR',
    INR = 'INR',
    KHR = 'KHR',
    LAK = 'LAK',
    MMK = 'MMK',
    MYR = 'MYR',
    PHP = 'PHP',
    TWD = 'TWD',
}

export enum DisplaySign {
    None,
    UnitSign,
    CurrencySign,
}

export const defaultUnitSign: Readonly<{ [value in Unit]: string }> = {
    [Unit.Value]: '',
    [Unit.Dollar]: '$',
    [Unit.Credit]: '',
};

export const defaultCurrencySign: Readonly<{ [value in Currency]: string }> = {
    [Currency.USD]: '$',
    [Currency.TWD]: '$',
    [Currency.CNY]: '¥',
    [Currency.THB]: '฿',
    [Currency.SOL]: 'S/.',
    [Currency.BRL]: 'ℛ', // R$
    [Currency.IDR]: '℞', // Rp
    [Currency.JPY]: '¥',
    [Currency.KRW]: '₩',
    [Currency.RUB]: '₽',
    [Currency.TRY]: '₺',
    [Currency.VND]: 'đ',
    [Currency.EUR]: '€',
    [Currency.INR]: '₹',
    [Currency.KHR]: ' ',
    [Currency.LAK]: '₭',
    [Currency.MMK]: 'K',
    [Currency.MYR]: '℟', // RM
    [Currency.PHP]: '₱', // Php
};

export const defaultSingleCharCurrencySign: Readonly<{ [value in Currency]: string }> = {
    [Currency.USD]: '$',
    [Currency.TWD]: '$',
    [Currency.CNY]: '¥',
    [Currency.THB]: '฿',
    [Currency.SOL]: 'š',
    [Currency.BRL]: 'ℛ',
    [Currency.IDR]: '℞',
    [Currency.JPY]: '¥',
    [Currency.KRW]: '₩',
    [Currency.RUB]: '₽',
    [Currency.TRY]: '₺',
    [Currency.VND]: 'đ',
    [Currency.EUR]: '€',
    [Currency.INR]: '₹',
    [Currency.KHR]: ' ',
    [Currency.LAK]: '₭',
    [Currency.MMK]: 'K',
    [Currency.MYR]: '℟',
    [Currency.PHP]: '₱',
};

export const defaultPrefixCurrencySign: Readonly<{ [value in Currency]: boolean }> = {
    [Currency.USD]: true,
    [Currency.TWD]: true,
    [Currency.CNY]: true,
    [Currency.THB]: true,
    [Currency.SOL]: true,
    [Currency.BRL]: true,
    [Currency.IDR]: true,
    [Currency.JPY]: true,
    [Currency.KRW]: true,
    [Currency.RUB]: false,
    [Currency.TRY]: true,
    [Currency.VND]: false,
    [Currency.EUR]: true,
    [Currency.INR]: true,
    [Currency.KHR]: true,
    [Currency.LAK]: true,
    [Currency.MMK]: true,
    [Currency.MYR]: true,
    [Currency.PHP]: true,
};

// Dollar(decimal) * AccUint(int32) = Credit(int64) * Denom(int32) = Value(int64)

export default class Account extends EventTarget {
    public static readonly EVENT_TYPE = EventType;
    /** 常數，使最小押注金額為 0.0001 */
    public static readonly ACC_UNIT: number = 10000;

    private _denom = 1;

    constructor(
        private _unit = Unit.Dollar,
        private _currency = Currency.USD,
        private _displaySign = DisplaySign.None,
        private _unitSignMap: { [value in Unit]: string } = defaultUnitSign,
        private _currencySignMap: Record<Currency, string> & { [key: string]: string } = defaultCurrencySign,
        private _singleCharCurrencySignMap: Record<Currency, string> & { [key: string]: string } = defaultSingleCharCurrencySign,
        private _prefixSignMap: Record<Currency, boolean> & { [key: string]: boolean } = defaultPrefixCurrencySign,
    ) {
        super();
    }

    public get denom() {
        return this._denom;
    }
    public set denom(value: number) {
        if (value === this._denom) {
            return;
        }
        this._denom = value;
        this.emit(EventType.DENOM_CHANGED);
    }

    public get unit() {
        return this._unit;
    }
    public set unit(value: Unit) {
        if (value === this._unit) {
            return;
        }
        this._unit = value;
        this.emit(EventType.UNIT_CHANGED);
    }

    public get currency() {
        return this._currency;
    }
    public set currency(value: Currency | string) {
        if (value === this._currency) {
            return;
        }
        this._currency = value as Currency;
        this.emit(EventType.CURRENCY_CHANGED);
    }

    public get sign() {
        if (this._displaySign === DisplaySign.CurrencySign) {
            return this._currencySignMap[this._currency] ?? '';
        }
        if (this._displaySign === DisplaySign.UnitSign) {
            return this._unitSignMap[this._unit] ?? '';
        }
        return '';
    }

    public get prefixSign() {
        if (this._displaySign === DisplaySign.CurrencySign) {
            return this._prefixSignMap[this._currency] ?? true;
        }
        return true;
    }

    public get singleCharCurrencySign() {
        return this._singleCharCurrencySignMap[this._currency] ?? '';
    }

    public valueToCredit(value: number): number {
        return this.valueTo(value, Unit.Credit);
    }

    public valueToDollar(value: number): number {
        return this.valueTo(value, Unit.Dollar);
    }

    public creditToValue(credit: number): number {
        return this.creditTo(credit, Unit.Value);
    }

    public creditToDollar(credit: number): number {
        return this.creditTo(credit, Unit.Dollar);
    }

    public dollarToValue(dollar: number): number {
        return this.dollarTo(dollar, Unit.Value);
    }

    public dollarToCredit(dollar: number): number {
        return this.dollarTo(dollar, Unit.Credit);
    }

    public valueTo(value: number, unit: Unit = this._unit): number {
        switch (unit) {
            case Unit.Value:
                return value;
            case Unit.Credit:
                return value / this._denom;
            case Unit.Dollar:
                return value / Account.ACC_UNIT;
            default:
                throw Error('Unknown unit type');
        }
    }

    public creditTo(credit: number, unit: Unit = this._unit): number {
        switch (unit) {
            case Unit.Value:
                return credit * this._denom;
            case Unit.Credit:
                return credit;
            case Unit.Dollar:
                return (credit * this._denom) / Account.ACC_UNIT;
            default:
                throw Error('Unknown unit type');
        }
    }

    public dollarTo(dollar: number, unit: Unit = this._unit): number {
        switch (unit) {
            case Unit.Value:
                return dollar * Account.ACC_UNIT;
            case Unit.Credit:
                return (dollar * Account.ACC_UNIT) / this._denom;
            case Unit.Dollar:
                return dollar;
            default:
                throw Error('Unknown unit type');
        }
    }

    public setCurrencySign(currecy: string, sign: string) {
        this._currencySignMap[currecy] = sign;
    }
}
